import { useState, useEffect } from 'react'
import { AttributionMode, LiveAttributionWindow, ProcessProgress, ProcessOptions } from '../types'

interface ProcessButtonProps {
  onStartProcess: (options: ProcessOptions) => Promise<{ sessionId: string }>
  onSessionStart: (sessionId: string) => void
  progress: ProcessProgress | null
  isConnected: boolean
  disabled: boolean
  companyKey: string
  startDate?: string
  endDate?: string
  forceDownload?: boolean
  forceProcess?: boolean
  /** Modo a passar pro pipeline. Cada botão dispara o seu próprio modo.
   *  Default 'stories' pra retrocompat com chamadas antigas. */
  mode?: AttributionMode
  /** Janela usada SOMENTE no modo 'live'. */
  liveAttributionWindow?: LiveAttributionWindow
  /** Label do botão (pra distinguir Stories vs Live na UI). */
  label?: string
  /** Cor do botão pra reforçar a identidade visual de cada produto. */
  variant?: 'primary' | 'live'
  /** Presente: mostra "Cancelar" ao lado do botão enquanto processa. */
  onCancel?: () => Promise<void>
}

export function ProcessButton({
  onStartProcess,
  onSessionStart,
  progress,
  isConnected,
  disabled,
  companyKey,
  startDate,
  endDate,
  forceDownload = false,
  forceProcess = false,
  mode = 'stories',
  liveAttributionWindow,
  label,
  variant = 'primary',
  onCancel,
}: ProcessButtonProps) {
  const [isStarting, setIsStarting] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const isProcessing = progress !== null && progress.step !== 'complete' && progress.step !== 'error'
  const isBusy = isStarting || isProcessing

  // Quando o primeiro progress chega, desliga o isStarting
  useEffect(() => {
    if (progress) {
      setIsStarting(false)
    }
  }, [progress])

  useEffect(() => {
    if (!isBusy) setCancelling(false)
  }, [isBusy])

  const handleClick = async () => {
    setIsStarting(true)
    try {
      const { sessionId } = await onStartProcess({
        companyKey,
        startDate,
        endDate,
        forceDownload,
        forceProcess,
        mode,
        liveAttributionWindow,
      })
      // Entrar na sala ANTES de o backend começar a emitir eventos
      onSessionStart(sessionId)
    } catch (error) {
      console.error('Erro ao iniciar processamento:', error)
      setIsStarting(false)
    }
  }

  const defaultLabel = mode === 'live' ? 'Processar Live' : 'Processar Stories'
  const styles = variant === 'live'
    ? { background: '#db2777', color: '#ffffff', border: '1px solid #db2777' }
    : undefined

  return (
    <>
      <button
        className="btn btn-primary"
        onClick={handleClick}
        disabled={disabled || isBusy || !isConnected}
        type="button"
        style={styles}
      >
        {isBusy && <span className="spinner" />}
        {isBusy ? 'Processando...' : (label ?? defaultLabel)}
      </button>
      {isBusy && onCancel && (
        <button
          type="button"
          className="btn debug-cancel"
          disabled={cancelling}
          title="Interrompe o processamento na API e libera o painel para rodar de novo"
          onClick={async () => {
            setCancelling(true)
            await onCancel()
          }}
        >
          {cancelling ? 'Cancelando...' : 'Cancelar processamento'}
        </button>
      )}
    </>
  )
}

interface ProcessStatusProps {
  progress: ProcessProgress | null
  isConnected: boolean
  isStarting?: boolean
  /** Mostra "Cancelar" ao lado da mensagem enquanto processa. */
  onCancel?: () => Promise<void>
}

export function ProcessStatus({ progress, isConnected, isStarting, onCancel }: ProcessStatusProps) {
  const [cancelling, setCancelling] = useState(false)
  const running = progress !== null && progress.step !== 'complete' && progress.step !== 'error'
  useEffect(() => {
    if (!running) setCancelling(false)
  }, [running])

  if (!isConnected) {
    return (
      <div className="status error" style={{ display: 'block' }}>
        WebSocket desconectado. Aguarde a reconexao...
      </div>
    )
  }

  if (isStarting && !progress) {
    return (
      <div className="status loading" style={{ display: 'block' }}>
        <span className="spinner" />
        Iniciando processamento...
      </div>
    )
  }

  if (!progress) return null

  const statusClass = progress.step === 'complete'
    ? 'success'
    : progress.step === 'error'
    ? 'error'
    : 'loading'

  return (
    <div className={`status ${statusClass}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ flex: 1 }}>
        {running && <span className="spinner" />}
        {progress.message}
      </span>
      {running && onCancel && (
        <button
          type="button"
          className="btn btn-sm debug-cancel"
          disabled={cancelling}
          onClick={async () => {
            setCancelling(true)
            await onCancel()
          }}
        >
          {cancelling ? 'Cancelando...' : 'Cancelar'}
        </button>
      )}
    </div>
  )
}
